import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import { competitionsService } from '../../services/competitions';
import { isUnderConsentAge } from '../../utils/consent-age';
import type { EntryDetails, EntryPreview, StageParticipation } from '../../types/trophy';
import EntryOptionsSummary from './EntryOptionsSummary';
import GenderChoice from './GenderChoice';
import { allTurnsFull, choosesTurn, chosenTurn } from './entry-options';

/** Why the last attempt was refused: every code the server sent, and its words. */
export interface EntryRefusal {
  codes: string[];
  message: string;
}

interface EnterStageDialogProps {
  /** The stage being confirmed; null keeps the dialog closed. */
  stage: StageParticipation | null;
  /** The turn picked on opening — the button sat on that turn's card. */
  initialRoundId?: string | null;
  trophyName: string;
  /**
   * The child this entry is for, when it is not the viewer's own: a parent
   * entering a ward has to see whose name is going on the start list.
   */
  athleteName?: string | null;
  /** That child's account — what the preview and the entry are asked about. */
  forUserId?: string | null;
  /** The address the entry will be under, so the person knows which account. */
  email: string;
  entering: boolean;
  /** The server's refusal of the last attempt; each code lands under its field. */
  refusal?: EntryRefusal | null;
  /** A value was changed: whatever the server said about the old one is stale. */
  onEdited?: () => void;
  onCancel: () => void;
  onConfirm: (details: EntryDetails) => void;
}

type Field = 'gender' | 'birth_date' | 'turn';

/** Which input a refusal is about, by its code. Anything else is the dialog's. */
const FIELD_OF_CODE: Record<string, Field> = {
  gender_required: 'gender',
  birth_date_required: 'birth_date',
  under_consent_age: 'birth_date',
  in_the_future: 'birth_date',
  round_required: 'turn',
  round_not_offered: 'turn',
  turn_started: 'turn',
  not_covered_by_agreement: 'turn',
};

function formatDate(value: string | null | undefined, locale: string) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * The one question before entering somebody in a stage — and, since gender and
 * date of birth are needed to compete, a small form behind it.
 *
 * It asks only for what nobody knows yet (the server says which, see
 * `entry/preview`), then shows what the entry will be: the **category** the
 * organiser's strategy puts the athlete in, and the **rounds** of that category
 * with their day and time. When the category is split over two or more turns
 * the athlete books one here; the rest of the day is listed so they can see how
 * long it is. The preview is read again as each missing value is filled, so
 * the category follows what was typed — the rules are never copied here.
 *
 * "Iscriviti" waits for the missing values and, where one is required, the
 * turn. A refusal goes under the field it is about, by its code, rather than in
 * a toast behind the dialog.
 *
 * A `Dialog` rather than the alert it used to be, because it has inputs now;
 * `grid-cols-[minmax(0,1fr)]` because shadcn's content is a grid whose column
 * otherwise sizes to a nowrap child and overflows a phone.
 */
export default function EnterStageDialog({
  stage,
  initialRoundId = null,
  trophyName,
  athleteName = null,
  forUserId = null,
  email,
  entering,
  refusal = null,
  onEdited,
  onCancel,
  onConfirm,
}: EnterStageDialogProps) {
  const { t, i18n } = useTranslation();
  const slug = stage?.slug ?? null;

  const [preview, setPreview] = useState<EntryPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [gender, setGender] = useState<'male' | 'female' | null>(null);
  const [birthDate, setBirthDate] = useState('');
  const [roundId, setRoundId] = useState<string | null>(null);
  const latestRequest = useRef(0);

  // A fresh form every time it opens, and for every athlete: what was typed
  // for one child is not an answer about the next.
  useEffect(() => {
    setPreview(null);
    setLoadError(null);
    setGender(null);
    setBirthDate('');
    setRoundId(initialRoundId);
  }, [slug, forUserId, initialRoundId]);

  const missingGender = !!preview?.missing.includes('gender');
  const missingBirthDate = !!preview?.missing.includes('birth_date');

  // What the form can already tell about a typed date, before the server does:
  // a child's date is always on their account, so this is only ever the
  // viewer's own, which may not be under 14 (nor in the future).
  const birthDateProblem = useMemo(() => {
    if (birthDate.length !== 10) return null;
    if (birthDate > new Date().toISOString().slice(0, 10)) return 'in_the_future';
    if (!forUserId && isUnderConsentAge(birthDate)) return 'under_consent_age';
    return null;
  }, [birthDate, forUserId]);
  const typedBirthDate = birthDate.length === 10 && !birthDateProblem ? birthDate : null;

  // Read on opening, and again whenever a missing value is filled in: the
  // category, and so the rounds, follow from it. Only the latest answer counts.
  useEffect(() => {
    if (!slug) return;
    const request = ++latestRequest.current;
    setLoading(true);

    competitionsService
      .previewEntry(slug, forUserId, { gender, birth_date: typedBirthDate })
      .then((data) => {
        if (request !== latestRequest.current) return;
        setPreview(data);
        setLoadError(null);
      })
      .catch((error: unknown) => {
        if (request !== latestRequest.current) return;
        // The raw words, translated at render time: `t` stays out of this
        // effect so it cannot loop on a translator that changes identity.
        setLoadError(error instanceof Error ? error.message : '');
      })
      .finally(() => {
        if (request === latestRequest.current) setLoading(false);
      });
  }, [slug, forUserId, gender, typedBirthDate]);

  // A turn picked for a category the athlete is no longer in is no choice.
  const chosenRound = chosenTurn(preview, roundId);
  const choosingTurn = choosesTurn(preview);
  // Every turn of the class is full: there is no entry to confirm, so the
  // button goes and the summary says why.
  const noSeats = allTurnsFull(preview) && !chosenRound;

  const ready =
    !!preview &&
    !loading &&
    !entering &&
    (!missingGender || !!gender) &&
    (!missingBirthDate || !!typedBirthDate) &&
    (!choosingTurn || !!chosenRound);

  const fieldErrors = useMemo(() => {
    const errors: Partial<Record<Field, string>> = {};
    let general: string | null = null;
    for (const code of refusal?.codes ?? []) {
      const field = FIELD_OF_CODE[code];
      if (field) errors[field] ??= t(`stageEntry.dialog.errors.${code}`);
      else general ??= refusal?.message ?? null;
    }
    if (refusal && refusal.codes.length === 0) general = refusal.message;
    return { ...errors, general };
  }, [refusal, t]);

  const birthDateError = birthDateProblem
    ? t(`stageEntry.dialog.errors.${birthDateProblem}`)
    : fieldErrors.birth_date;

  const edited = () => onEdited?.();

  const confirm = () => {
    if (!ready || !preview) return;
    onConfirm({
      gender: missingGender ? gender : null,
      birth_date: missingBirthDate ? typedBirthDate : null,
      competition_round_id: choosingTurn ? chosenRound : null,
    });
  };

  const date = formatDate(stage?.start_date, i18n.language) ?? '';
  const savedOnProfile = preview?.saved_on_profile ?? [];

  return (
    <Dialog open={!!stage} onOpenChange={(open) => !open && !entering && onCancel()}>
      <DialogContent
        className="max-h-[90dvh] w-[calc(100%-2rem)] grid-cols-[minmax(0,1fr)] overflow-y-auto p-4 sm:max-w-md sm:p-6"
        data-testid="enter-stage-dialog"
      >
        <DialogHeader className="pr-6 text-left">
          <DialogTitle className="text-base sm:text-lg">
            {athleteName
              ? t('stageEntry.confirmTitleFor', { name: athleteName, stage: stage?.name ?? '' })
              : t('stageEntry.confirmTitle', { stage: stage?.name ?? '' })}
          </DialogTitle>
          <DialogDescription className="break-words text-xs sm:text-sm">
            {athleteName
              ? t('stageEntry.confirmDescriptionFor', { name: athleteName, trophy: trophyName, stage: stage?.name ?? '', date, email })
              : t('stageEntry.confirmDescription', { trophy: trophyName, stage: stage?.name ?? '', date, email })}
          </DialogDescription>
        </DialogHeader>

        {loadError !== null && !preview ? (
          <p className="text-sm text-destructive" data-testid="entry-preview-error">
            {loadError || t('stageEntry.error')}
          </p>
        ) : !preview ? (
          <div className="space-y-2" data-testid="entry-preview-loading">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : (
          <div className="min-w-0 space-y-4">
            {(missingGender || missingBirthDate) && (
              <section className="space-y-3 rounded-lg border p-3" data-testid="entry-missing">
                <p className="text-sm font-medium">{t('stageEntry.dialog.missingIntro')}</p>

                {missingGender && (
                  <fieldset className="space-y-1.5" data-testid="entry-gender">
                    <legend className="text-sm font-medium">{t('stageEntry.dialog.gender')}</legend>
                    <GenderChoice
                      name="entry-gender"
                      value={gender}
                      onChange={(value) => {
                        setGender(value);
                        edited();
                      }}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('stageEntry.dialog.genderHint')}
                      {!savedOnProfile.includes('gender') && <> {t('stageEntry.dialog.genderProfileKept')}</>}
                    </p>
                    {fieldErrors.gender && (
                      <p className="text-xs text-destructive" data-testid="entry-gender-error">
                        {fieldErrors.gender}
                      </p>
                    )}
                  </fieldset>
                )}

                {missingBirthDate && (
                  <div className="space-y-1.5">
                    <Label htmlFor="entry-birth-date">{t('stageEntry.dialog.birthDate')}</Label>
                    <Input
                      id="entry-birth-date"
                      type="date"
                      max={new Date().toISOString().slice(0, 10)}
                      value={birthDate}
                      onChange={(event) => {
                        setBirthDate(event.target.value);
                        edited();
                      }}
                      className={birthDateError ? 'border-destructive' : undefined}
                    />
                    {birthDateError && (
                      <p className="text-xs text-destructive" data-testid="entry-birth-date-error">
                        {birthDateError}
                      </p>
                    )}
                  </div>
                )}

                {savedOnProfile.length > 0 && (
                  <p className="text-xs text-muted-foreground" data-testid="entry-saved-on-profile">
                    {athleteName
                      ? t('stageEntry.dialog.savedOnProfileFor', { name: athleteName, count: savedOnProfile.length })
                      : t('stageEntry.dialog.savedOnProfile', { count: savedOnProfile.length })}
                  </p>
                )}
              </section>
            )}

            <EntryOptionsSummary
              options={preview}
              roundId={roundId}
              onRoundChange={(id) => {
                setRoundId(id);
                edited();
              }}
              turnError={fieldErrors.turn}
            />

            {/* The server's refusal of the entry — or, failing that, of the
                last re-read of the preview, which leaves the old one showing. */}
            {(fieldErrors.general || loadError) && (
              <p className="text-sm text-destructive" data-testid="entry-error">
                {fieldErrors.general || loadError || t('stageEntry.error')}
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onCancel} disabled={entering} className="w-full sm:w-auto">
            {t('common.cancel')}
          </Button>
          {!noSeats && (
            <Button
              type="button"
              disabled={!ready}
              onClick={confirm}
              data-testid="confirm-stage-entry"
              className="w-full min-w-0 sm:w-auto"
            >
              {entering && <Loader2 className="mr-2 size-4 shrink-0 animate-spin" />}
              <span className="truncate">
                {athleteName ? t('stageEntry.confirmFor', { name: athleteName }) : t('stageEntry.confirm')}
              </span>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
